import { v4 as uuidv4 } from 'uuid';
import { IControl, IPlayerStats, IPoll, ResultInstance } from '../definitions';
import { toAwardWinners } from './awards';

export const PARENT_AWARD_NAME = "Parents' Player of the Week";
const POLL_OPEN_DAYS = 5;

/** Feature defaults ON for teams that haven't set a preference — only an explicit false disables it. */
export const isParentPollFeatureEnabled = (control: Pick<IControl, 'parentPlayerOfWeekEnabled'>): boolean =>
  control.parentPlayerOfWeekEnabled !== false;

/** Results have no stable id — date + opponent name is the existing convention (see share URLs). */
export const getResultPollKey = (date: string, team: string): string => `${date}|${team}`;

export const findLinkedParentPoll = (polls: IPoll[], resultKey: string): IPoll | undefined =>
  polls.find(poll => poll.kind === 'parentPlayerOfWeek' && poll.linkedResultKey === resultKey);

/** Mirrors PollCard's own expiry check so callers agree on open/closed before the DB write lands. */
export const isPollOpen = (poll: IPoll): boolean => {
  const expired = !!(poll.closesAt && new Date(poll.closesAt) <= new Date());
  return !expired && poll.status === 'open';
};

export const buildParentPlayerOfWeekPoll = (params: {
  resultKey: string;
  opponent: string;
  playedPlayerNames: string[];
  createdBy: string;
  createdByName: string;
}): IPoll => {
  const { resultKey, opponent, playedPlayerNames, createdBy, createdByName } = params;
  const options: IPoll['options'] = {};
  playedPlayerNames.forEach((name, idx) => {
    options[uuidv4()] = { text: name, order: idx };
  });

  return {
    id: uuidv4(),
    title: `Parents' Player of the Week — vs ${opponent}`,
    description: "Vote for your Parents' Player of the Week for this match.",
    createdBy,
    createdByName,
    createdAt: new Date().toISOString(),
    closesAt: new Date(Date.now() + POLL_OPEN_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    status: 'open',
    type: 'single',
    options,
    votes: {},
    settings: {
      anonymous: false,
      allowMultiple: false,
      showResults: 'after_vote',
      notifyTeam: false,
      restrictToPlayers: true,
    },
    kind: 'parentPlayerOfWeek',
    linkedResultKey: resultKey,
    awardApplied: false,
  };
};

/** Tallies votes and returns every player tied on the top vote count — empty if nobody voted. */
export const getPollWinners = (poll: IPoll): string[] => {
  const counts: Record<string, number> = {};
  Object.values(poll.votes || {}).forEach(vote => {
    vote.optionIds.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
  });

  const orderedIds = Object.keys(poll.options).sort((a, b) => poll.options[a].order - poll.options[b].order);
  const topCount = Math.max(0, ...orderedIds.map(id => counts[id] ?? 0));
  if (topCount === 0) return [];

  return orderedIds.filter(id => (counts[id] ?? 0) === topCount).map(id => poll.options[id].text);
};

/** Retains every player's votes, not just the winning player's, once per poll. */
export const applyParentPollVotes = (
  poll: IPoll,
  players: Record<string, IPlayerStats>
): Record<string, IPlayerStats> => {
  if (poll.kind !== 'parentPlayerOfWeek' || isPollOpen(poll)) {
    throw new Error('Parent vote totals can only be saved for a closed parent poll');
  }
  const counts: Record<string, number> = {};
  Object.values(poll.votes || {}).forEach(vote => {
    new Set(vote.optionIds).forEach(id => {
      const name = poll.options[id]?.text;
      if (name) counts[name] = (counts[name] ?? 0) + 1;
    });
  });
  const updated = { ...players };
  Object.entries(counts).forEach(([name, count]) => {
    const player = players[name];
    if (!player) throw new Error(`Cannot save parent votes: player "${name}" no longer exists`);
    if (player.parentVotePolls?.[poll.id] !== undefined) return;
    updated[name] = {
      ...player,
      parentVotes: (player.parentVotes ?? 0) + count,
      parentVotePolls: { ...player.parentVotePolls, [poll.id]: count },
    };
  });
  return updated;
};

/**
 * Finds closed, unsettled parentPlayerOfWeek polls and computes the writes needed to apply
 * each winner to its linked result's otherAwards. Settled polls (winner applied, or confirmed
 * there's nothing to apply) are queued for deletion instead of lingering in the DB forever.
 * A poll with a winner whose linked result hasn't loaded/synced yet is left pending so the
 * caller retries it once `results` catches up — it must not be treated as settled early, or
 * the winner is lost for good. Pure function — caller persists deletions (deletePoll) and
 * `updatedResults` as a whole (writeResults).
 */
export const resolveClosedParentPolls = (
  polls: IPoll[],
  results: ResultInstance[]
): { pollIdsToDelete: string[]; updatedResults: ResultInstance[] } | null => {
  const pending = polls.filter(poll => poll.kind === 'parentPlayerOfWeek' && !poll.awardApplied && !isPollOpen(poll));
  if (pending.length === 0) return null;

  const pollIdsToDelete: string[] = [];
  let updatedResults = results;
  let resultsChanged = false;

  pending.forEach(poll => {
    const winners = poll.linkedResultKey ? getPollWinners(poll) : [];

    if (poll.linkedResultKey && winners.length > 0) {
      const resultIdx = updatedResults.findIndex(r => getResultPollKey(r.date, r.team) === poll.linkedResultKey);
      if (resultIdx === -1) {
        // Linked result hasn't loaded/synced yet — retry next time `results` updates, don't settle now.
        return;
      }
      if (!resultsChanged) updatedResults = [...updatedResults];
      resultsChanged = true;
      const existing = updatedResults[resultIdx];
      // Must rebuild the instance: spreading one keeps `toDbObject` bound to the original, so the award would never persist.
      updatedResults[resultIdx] = new ResultInstance({
        ...existing.toDbObject(),
        otherAwards: { ...(existing.otherAwards || {}), [PARENT_AWARD_NAME]: toAwardWinners(winners) },
      });
    }

    // Either the winner is now applied, or there was nothing to apply (no link / no votes) — settled.
    pollIdsToDelete.push(poll.id);
  });

  if (pollIdsToDelete.length === 0 && !resultsChanged) return null;

  return { pollIdsToDelete, updatedResults };
};
